import { useCallback, useEffect, useRef, useState } from "react";
import { finishGame, startGame, type GameResult } from "../api.js";

/** Duración de la partida, en segundos. */
const DURATION = 8;

/**
 * Enfriamiento entre clics válidos.
 *
 * No es antojo: el servidor rechaza más de 5 puntos por segundo como implausible.
 * Con 250ms el máximo alcanzable es 4 por segundo, así que jugar bien nunca choca
 * con la validación — el límite se respeta por diseño del juego, no pidiéndole al
 * jugador que se contenga.
 */
const COOLDOWN_MS = 250;

interface Props {
  onFinished: (result: GameResult) => void;
  onClose: () => void;
}

type Phase = "idle" | "playing" | "sending" | "done";

/**
 * Minijuego mínimo pero real: el blanco salta a otra posición en cada acierto.
 * Existe para cerrar el bucle ganar → gastar; reemplázalo por juegos de verdad.
 */
export function DemoGame({ onFinished, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [score, setScore] = useState(0);
  const [left, setLeft] = useState(DURATION);
  const [spot, setSpot] = useState({ x: 50, y: 50 });
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GameResult | null>(null);

  const sessionRef = useRef<string | null>(null);
  const lastHitRef = useRef(0);

  const begin = useCallback(async () => {
    setError(null);
    setScore(0);
    setLeft(DURATION);
    setResult(null);

    try {
      const started = await startGame("demo");
      sessionRef.current = started.sessionId;
      setPhase("playing");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo empezar.");
    }
  }, []);

  // Cuenta atrás. Al llegar a cero se envía el puntaje.
  useEffect(() => {
    if (phase !== "playing") return;

    const timer = setInterval(() => {
      setLeft((remaining) => {
        if (remaining > 1) return remaining - 1;
        clearInterval(timer);
        setPhase("sending");
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase]);

  // El envío va en su propio efecto para no hacerlo dentro del setInterval.
  useEffect(() => {
    if (phase !== "sending") return;
    const sessionId = sessionRef.current;
    if (!sessionId) return;

    void (async () => {
      try {
        const finished = await finishGame(sessionId, score);
        setResult(finished);
        onFinished(finished);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "No se pudo cerrar la partida.");
      } finally {
        setPhase("done");
      }
    })();
  }, [phase, score, onFinished]);

  const hit = () => {
    const now = Date.now();
    if (now - lastHitRef.current < COOLDOWN_MS) return;
    lastHitRef.current = now;

    setScore((current) => current + 1);
    setSpot({ x: 12 + Math.random() * 76, y: 18 + Math.random() * 64 });
  };

  return (
    <div className="overlay">
      <div className="panel game">
        <div className="game__head">
          <h2 className="game__title">Juego de prueba</h2>
          {phase === "playing" && <span className="game__timer">{left}s</span>}
          <span className="game__score">◉ {score}</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar juego">
            ×
          </button>
        </div>

        <div className="game__field">
          {phase === "idle" && (
            <div className="game__center">
              <p className="game__hint">Toca el blanco tantas veces como puedas en {DURATION}s.</p>
              <button className="btn" type="button" onClick={() => void begin()}>
                Empezar
              </button>
            </div>
          )}

          {phase === "playing" && (
            <button
              className="game__target"
              type="button"
              style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
              onPointerDown={hit}
              aria-label="Blanco"
            />
          )}

          {(phase === "sending" || phase === "done") && (
            <div className="game__center">
              {phase === "sending" && <p className="game__hint">Enviando…</p>}

              {result && (
                <>
                  <p className="game__result">+{result.coinsAwarded} monedas</p>
                  {result.cappedByLimit && (
                    <p className="game__hint">Alcanzaste el techo por hora.</p>
                  )}
                </>
              )}

              {phase === "done" && (
                <button className="btn" type="button" onClick={() => void begin()}>
                  Otra vez
                </button>
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="game__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
